import { useEffect, useRef } from "react";

// Lightweight, dependency-free neural-network constellation (canvas + rAF).
// Mouse-reactive links, GPU-friendly, respects prefers-reduced-motion.
export function NeuralBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, w, h, dpr;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const LINK = 150;
    const MOUSE_LINK = 190;
    const mouse = { x: -9999, y: -9999 };
    let nodes = [];

    function count() {
      const area = window.innerWidth * window.innerHeight;
      return Math.max(28, Math.min(64, Math.round(area / 26000)));
    }
    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    function init() {
      nodes = Array.from({ length: count() }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.32,
        vy: (Math.random() - 0.5) * 0.32,
      }));
    }
    function draw() {
      ctx.clearRect(0, 0, w, h);
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < LINK) {
            const t = 1 - d / LINK;
            ctx.strokeStyle = `rgba(0,240,255,${0.13 * t})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      for (const n of nodes) {
        const dx = n.x - mouse.x, dy = n.y - mouse.y;
        const d = Math.hypot(dx, dy);
        if (d < MOUSE_LINK) {
          const t = 1 - d / MOUSE_LINK;
          ctx.strokeStyle = `rgba(176,38,255,${0.22 * t})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(n.x, n.y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.stroke();
        }
      }
      for (const n of nodes) {
        ctx.fillStyle = "rgba(0,240,255,0.75)";
        ctx.beginPath();
        ctx.arc(n.x, n.y, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    function loop() {
      draw();
      raf = requestAnimationFrame(loop);
    }

    resize();
    init();
    if (reduced) {
      draw();
    } else {
      loop();
    }

    const onResize = () => { resize(); init(); };
    const onMove = (e) => { mouse.x = e.clientX; mouse.y = e.clientY; };
    const onLeave = () => { mouse.x = -9999; mouse.y = -9999; };
    window.addEventListener("resize", onResize);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseout", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
    };
  }, []);

  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden bg-background" data-testid="neural-background">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(55% 45% at 50% -5%, rgba(0,240,255,0.08), transparent 70%), radial-gradient(45% 45% at 88% 95%, rgba(176,38,255,0.08), transparent 72%)",
        }}
      />
      <canvas ref={canvasRef} className="absolute inset-0 opacity-70" />
      <div className="absolute inset-0 bg-[radial-gradient(120%_120%_at_50%_0%,transparent_55%,rgba(3,3,5,0.85)_100%)]" />
    </div>
  );
}
